import React, { useState } from "react";
import ArchiveIcon from "@mui/icons-material/Archive";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Attachment } from "../api/common";
import { getApiBase } from "../utils/api";
import { useTranslate } from "react-admin";

const getAttachmentUrl = (photo: Attachment) => `${getApiBase()}/attachments?uuid=${photo.uuid}`;

const PhotoPreview = ({ photo, large = false }: { photo: Attachment; large?: boolean }) => {
  return (
    <Box
      alt={photo.file_name}
      component="img"
      src={getAttachmentUrl(photo)}
      sx={{
        display: "block",
        ...(large
          ? {
              height: "auto",
              margin: "0 auto",
              maxHeight: "70vh",
              maxWidth: "100%",
              objectFit: "contain",
              width: "auto",
            }
          : {
              height: 112,
              objectFit: "cover",
              width: "100%",
            }),
      }}
    />
  );
};

export const HandoverAddressPhotos = ({
  photos,
  downloadUrl,
}: {
  label: string;
  photos: Attachment[];
  downloadUrl: string;
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const translate = useTranslate();
  const selectedPhoto = selectedPhotoIndex === null ? null : photos[selectedPhotoIndex];

  if (photos.length === 0) {
    return null;
  }

  return (
    <Box sx={{ my: 2 }}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button component="a" href={downloadUrl} size="small" startIcon={<ArchiveIcon />}>
          {translate("ui.handover_address_photos.bulk_download")}
        </Button>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: { sm: "repeat(3, minmax(0, 1fr))", xs: "repeat(2, minmax(0, 1fr))" },
          minWidth: 0,
        }}
      >
        {photos.map((photo, index) => (
          <ButtonBase
            aria-label={`${photo.file_name}を表示`}
            key={photo.uuid}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              display: "block",
              overflow: "hidden",
              textAlign: "left",
            }}
            onClick={() => setSelectedPhotoIndex(index)}
          >
            <PhotoPreview photo={photo} />
            <Typography noWrap sx={{ display: "block", px: 1, py: 0.75 }} title={photo.file_name} variant="caption">
              写真 {index + 1}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
      <Dialog fullWidth maxWidth="md" open={selectedPhoto !== null} onClose={() => setSelectedPhotoIndex(null)}>
        <DialogTitle>
          引取場所の写真 {selectedPhotoIndex !== null && `${selectedPhotoIndex + 1} / ${photos.length}`}
          <IconButton
            aria-label="閉じる"
            size="small"
            sx={{ position: "absolute", right: 16, top: 16 }}
            onClick={() => setSelectedPhotoIndex(null)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>{selectedPhoto && <PhotoPreview large photo={selectedPhoto} />}</DialogContent>
        {selectedPhoto && (
          <DialogActions>
            <Button component="a" href={getAttachmentUrl(selectedPhoto)} startIcon={<DownloadIcon />}>
              {translate("ui.handover_address_photos.download")}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};
